<template>
  <div
    v-loading="loading"
    class="r-table"
  >
    <div class="r-table__wrapper">
      <table
        v-if="columns && data.length && !loading"
        class="r-table__table"
        :class="{ sorting, 'fixed-first': fixedFirstCol }"
      >
        <thead
          v-if="!noHead"
          @[handler]="getSort($event)"
        >
          <tr>
            <th
              v-for="(col, key) in columns"
              :key="key"
              :data-col="key"
              :data-type="col.type"
              :class="{ simple }"
              :style="{ backgroundColor: headColor }"
            >
              <div class="r-table__head">
                <r-text
                  v-if="simple"
                  type="caption"
                  :title="col.tooltip || col.title"
                >
                  {{ col.title }}
                </r-text>
                <r-title
                  v-else
                  type="subtitle-2"
                  :title="col.tooltip || col.title"
                >
                  {{ col.title }}
                </r-title>
                <r-icon
                  v-if="sorting && !hasSlot(key)"
                  name="sort-ascending"
                  :rotate="getIconRotate(key)"
                  :color="getColor(key)"
                  class="r-table__icon"
                  :size="20"
                />
                <r-dropdown
                  v-if="col.tools && hasSlot('tools')"
                  :menu-max-height="360"
                  :menu-max-width="300"
                  :show="isToolsShow"
                  stick-to="left"
                  @close="isToolsShow = true"
                >
                  <r-button
                    :class="['r-table__head-tools', { show: showToolsButton }]"
                    :title="toolsTitle"
                    type="secondary"
                    icon="settings"
                    mini
                  />
                  <div slot="dropdown-menu">
                    <slot
                      :id="key"
                      name="tools"
                    />
                  </div>
                </r-dropdown>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, ndx) in tableData"
            :key="ndx"
            :class="{ selected: selected === row.id }"
            @mouseover="$emit('hover-in', row)"
            @mouseleave="$emit('hover-out', row)"
            @click="$emit('row-click', row)"
          >
            <td
              v-for="(
                { width, hardWidth, type = 'default', bg }, key
              ) in columns"
              :key="key"
              :style="getTdStyle(width, hardWidth)"
              :class="{ bg, simple }"
            >
              <slot
                v-if="hasSlot(key)"
                :row="row"
                :name="key"
              />
              <r-text
                v-else
                class="r-table__cell"
                :title="getValueByType(row[key], type)"
              >
                {{ getValueByType(row[key], type) }}
              </r-text>
            </td>
          </tr>
        </tbody>
      </table>
      <div
        v-else
        class="r-table__no-data"
      >
        <r-text> {{ loading ? '' : $t('no-data') }} </r-text>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep, getValueByType } from 'HELPERS'

export default {
  props: {
    data: {
      type: Array,
      required: true
    },
    columns: {
      type: Object,
      default: null
    },
    sorting: {
      type: Boolean,
      default: false
    },
    initSort: {
      type: String,
      default: ''
    },
    noHead: {
      type: Boolean,
      default: false
    },
    selected: {
      type: [Number, String],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    fixedFirstCol: {
      type: Boolean,
      default: false
    },
    showToolsButton: {
      type: Boolean,
      default: false
    },
    simple: {
      type: Boolean,
      default: false
    },
    headColor: {
      type: String,
      default: null
    },
    toolsTitle: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      sortingCol: null,
      isToolsShow: false
    }
  },
  computed: {
    tableData() {
      const tableData = cloneDeep(this.data)

      if (!this.initSort) return tableData

      const collator = new Intl.Collator('ru-RU', { numeric: true })
      const { initSort } = this
      tableData.sort((a, b) => collator.compare(a[initSort], b[initSort]))

      return tableData
    },
    handler() {
      return this.sorting ? 'click' : null
    }
  },
  methods: {
    hasSlot(key) {
      return Object.keys(this.$scopedSlots).includes(key)
    },
    getIconRotate(title) {
      if (!this.sortingCol) return 0
      const { col, direction } = this.sortingCol

      return col === title && direction === 1 ? 0 : col === title ? 180 : 0
    },
    getColor(title) {
      if (!this.sortingCol) return 'var(--dividers-high-contrast)'
      const { col } = this.sortingCol

      return col === title
        ? 'var(--accent-primary)'
        : 'var(--dividers-high-contrast)'
      // FIXME: replace by css classes
    },
    getTdStyle(width, hardWidth) {
      if (width) {
        return hardWidth ? { width: `${width}px` } : { minWidth: `${width}px` }
      } else {
        return {}
      }
    },
    getSort({ target }) {
      const thTarget = target.closest('th')

      if (!thTarget) return
      const direction = (thTarget.dataset.direction = -(
        thTarget.dataset.direction || -1
      ))
      const col = thTarget.dataset.col
      const type = thTarget.dataset.type
      const collator = new Intl.Collator('ru-RU', { numeric: true })

      this.tableData.sort((a, b) => {
        const next = type === 'date' ? Date.parse(a[col]) : a[col]
        const cur = type === 'date' ? Date.parse(b[col]) : b[col]

        return direction * collator.compare(next, cur)
      })
      this.sortingCol = { col, direction }
    },
    getValueByType
  }
}
</script>

<style lang="scss" scoped>
.r-table {
  flex-direction: column;
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;

  &__wrapper {
    overflow: auto;
    flex-grow: 1;
    height: 100%;
    width: 100%;
  }

  &__table {
    border-collapse: separate;
    border-spacing: 0;
    max-height: 100%;
    width: 100%;

    &.sorting {
      thead {
        cursor: pointer;
      }
    }

    &.fixed-first {
      thead tr > :first-child {
        z-index: 3;
        left: 0;
        top: 0;
        @include border-right;
      }

      tbody tr {
        & > :first-child {
          position: sticky;
          z-index: 1;
          left: 0;
          @include border-right;
          background-color: $bg-panel-secondary;
        }
      }
    }
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.25rem;

    &-tools {
      position: absolute;
      opacity: 0;
      top: 0.25rem;
      right: 0.25rem;
      transition: all 0.15s ease;

      &.show {
        opacity: 1;
      }
    }

    &:hover {
      .r-table__head-tools {
        opacity: 1;
      }
    }
  }

  &__icon {
    flex-shrink: 0;
  }

  &__cell {
    @include line-clamp(2);
  }

  tr > :last-child {
    border-right: 1px solid $table-border;
  }

  th {
    background-color: $bg-panel-secondary;
    height: 40px;
    padding: 0.25rem 0.5rem;
    user-select: none;
    border: 1px solid $table-border;
    border-right-color: transparent;
    position: sticky;
    z-index: 2;
    top: 0;

    .r-title {
      @include line-clamp(2);
    }

    &.simple {
      background-color: $modal-bg;
      border: none;
      border-bottom: 1px solid $dividers-low-contrast;
    }

    div {
      word-break: normal;
    }
  }

  td {
    padding: 0.25rem 0.5rem;
    border: 1px solid $table-border;
    border-top-color: transparent;
    border-right-color: transparent;

    div {
      word-break: normal;
    }

    &.bg {
      background-color: $text-tertiary;
    }
    &.simple {
      border: none;
    }
  }

  &__no-data {
    text-align: center;
    padding: 1rem;
  }
}
</style>
